import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import { orderWithAvailabilityCheck } from "apis/order";

import { cumulativeCart, refreshTotals } from "@actions/order";

import { setIsCartUpdating, updateCartOrder, updateOrderAttributes } from "@slices/cart";

import { checkoutRoute } from "@constants/routes";

const useRefreshCumulativeCart = () => {
  const { pathname } = useRouter();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const isCheckoutPage = pathname.split("/").pop() === checkoutRoute.split("/").pop();

  const postCumulativeCartRefresh = useCallback(
    (order, { updateAttributesOnly = false } = {}) => {
      setLoading(false);
      dispatch(updateAttributesOnly ? updateOrderAttributes(order) : updateCartOrder(order));
    },
    [dispatch, setLoading],
  );

  const refreshCumulativeCart = useCallback(
    (orderId) => {
      setLoading(true);
      return dispatch(refreshTotals(orderId))
        .then((order) => postCumulativeCartRefresh(order, { updateAttributesOnly: true }))
        .catch((err) => {
          setLoading(false);
          throw err;
        });
    },
    [dispatch, postCumulativeCartRefresh],
  );

  const fetchCumulativeCart = useCallback(
    (orderId, { updateAttributesOnly = false } = {}) => {
      setLoading(true);
      return dispatch(cumulativeCart(orderId))
        .then((order) => postCumulativeCartRefresh(order, { updateAttributesOnly }))
        .catch((err) => {
          setLoading(false);
          throw err;
        });
    },
    [dispatch, postCumulativeCartRefresh],
  );

  const fetchAvailability = useCallback(
    (orderId) => {
      dispatch(setIsCartUpdating(true));
      setLoading(true);
      return dispatch(orderWithAvailabilityCheck(orderId))
        .then(postCumulativeCartRefresh)
        .catch((err) => {
          setLoading(false);
          throw err;
        })
        .finally(() => {
          dispatch(setIsCartUpdating(false));
        });
    },
    [dispatch, postCumulativeCartRefresh],
  );

  return {
    loading,
    fetchCumulativeCart: isCheckoutPage ? refreshCumulativeCart : fetchCumulativeCart,
    fetchCart: fetchCumulativeCart,
    refreshCart: refreshCumulativeCart,
    fetchAvailability,
    postCumulativeCartRefresh,
  };
};

export default useRefreshCumulativeCart;
